import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Alert, Button, Badge, Tabs, Tab, Table, Modal, Form, Row, Col } from 'react-bootstrap';
import axios from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatDateRange } from '../../utils/dateUtils';
import {
  getActiveAgeRules,
  validateTeamMemberAges,
  canAddMemberToTeam,
  getMemberAgeStatus,
  getEventReferenceDate,
  getOptionLabel,
  getOptionValue
} from '../../utils/teamAgeValidation';

const computeRegistrationStatus = (eventData) => {
  if (!eventData) return 'open';
  try {
    const now = new Date();
    const registrationDeadlineRaw = eventData.registrationDeadline || eventData.registration_deadline;
    const registrationDeadline = registrationDeadlineRaw ? new Date(registrationDeadlineRaw) : null;
    const allowLateRegistration = eventData.allowLateRegistration === true || eventData.allow_late_registration === true;
    const eventDate = new Date(eventData.eventDate || eventData.event_date);

    if (!isNaN(eventDate.getTime()) && now > eventDate) {
      return 'closed';
    }

    if (registrationDeadline && now > registrationDeadline) {
      return allowLateRegistration ? 'late' : 'closed';
    }

    return 'open';
  } catch (error) {
    console.error('Error checking registration status:', error);
    return 'open';
  }
};

const TeamRegistrationDetails = () => {
  const { id } = useParams(); // ID dell'evento
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('report');

  // Stato per il modal di modifica
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingTeamIndex, setEditingTeamIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    members: [],
    formFields: {}
  });
  const [submitting, setSubmitting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [sortedMembers, setSortedMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const registrationStatus = computeRegistrationStatus(event);
  const isRegistrationOpen = registrationStatus !== 'closed';

  // Helper: calcola età e formatta data di nascita come gg-mm-aaaa (età)
  const computeAge = (birthDateString) => {
    if (!birthDateString) return null;
    const d = new Date(birthDateString);
    if (isNaN(d)) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
      age--;
    }
    return age;
  };

  const formatBirthWithAge = (birthDateString) => {
    if (!birthDateString) return '-';
    const d = new Date(birthDateString);
    if (isNaN(d)) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const age = computeAge(birthDateString);
    return `${day}-${month}-${year}${age != null ? ` (${age})` : ''}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Recupero dettagli evento
        const eventResponse = await axios.get(`/api/events/${id}`);
        const eventData = eventResponse.data.event || eventResponse.data;
        setEvent(eventData);

        // Recupero membri dell'associazione
        if (user && user.association_id) {
          try {
            const membersResponse = await axios.get(`/api/associations/${user.association_id}/members/team-picker`);
            setMembers(membersResponse.data.members || []);

            // Recupero team registrati per questo evento
            try {
              const teamsResponse = await axios.get(
                `/api/associations/${user.association_id}/events/${id}/teams`
              );

              if (teamsResponse.data && teamsResponse.data.teams) {
                setTeams(teamsResponse.data.teams);
              }
            } catch (teamsError) {
              console.error('Errore nel recupero delle squadre:', teamsError);
              setError('Impossibile caricare le squadre. Riprova più tardi.');
            }

          } catch (err) {
            console.error('Errore nel recupero dei membri:', err);
            setError('Impossibile caricare i membri dell\'associazione. Riprova più tardi.');
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Errore nel recupero dei dati:', err);
        setError('Impossibile caricare i dati dell\'evento. Riprova più tardi.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user?.association_id]);

  // Ordina i membri quando cambiano members o sortConfig
  useEffect(() => {
    let sorted = [...members];
    
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'name') {
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
        } else if (sortConfig.key === 'age') {
          aValue = computeAge(a.birth_date) ?? 999;
          bValue = computeAge(b.birth_date) ?? 999;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setSortedMembers(sorted);
  }, [members, sortConfig]);

  // Gestisce il cambio di ordinamento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Componente per l'icona di ordinamento
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="bi bi-arrow-up ms-1"></i>
      : <i className="bi bi-arrow-down ms-1"></i>;
  };

  // Funzione per aprire il modal di modifica
  const handleEditTeam = (team, index) => {
    if (!isRegistrationOpen) {
      setError('Le iscrizioni sono chiuse. Le modifiche non sono consentite.');
      return;
    }
    setEditingTeam(team);
    setEditingTeamIndex(index);

    // Prepara i dati per il form di modifica
    const teamMembers = team.members.map(member => member.member_id);

    setEditFormData({
      name: team.name,
      members: teamMembers,
      formFields: team.formData || {}
    });

    setShowEditModal(true);
  };

  // Funzione per chiudere il modal
  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingTeam(null);
    setEditingTeamIndex(null);
    setMemberSearchQuery('');
  };

  // Funzione per gestire il cambio nome della squadra
  const handleTeamNameChange = (e) => {
    setEditFormData({
      ...editFormData,
      name: e.target.value
    });
  };

  const getEditMembersData = (memberIds) => {
    return memberIds
      .map(memberId => members.find(m => m.id === memberId))
      .filter(Boolean);
  };

  // Funzione per gestire la selezione/deselezione dei membri
  const handleMemberSelect = (memberId) => {
    if (!isRegistrationOpen) return;
    const updatedMembers = [...editFormData.members];

    if (updatedMembers.includes(memberId)) {
      const index = updatedMembers.indexOf(memberId);
      updatedMembers.splice(index, 1);
      setEditFormData({ ...editFormData, members: updatedMembers });
      setError(null);
      return;
    }

    const member = members.find(m => m.id === memberId);
    const rules = getActiveAgeRules(
      event?.registrationFields,
      editFormData.formFields,
      event?.category
    );

    if (rules && member) {
      const currentMembersData = getEditMembersData(updatedMembers);
      const check = canAddMemberToTeam(
        member,
        currentMembersData,
        rules,
        getEventReferenceDate(event)
      );
      if (!check.allowed) {
        setError(check.reason);
        return;
      }
    }

    updatedMembers.push(memberId);
    setEditFormData({ ...editFormData, members: updatedMembers });
    setError(null);
  };

  // Funzione per gestire il cambio dei campi del form
  const handleFieldChange = (fieldName, value) => {
    setEditFormData({
      ...editFormData,
      formFields: {
        ...editFormData.formFields,
        [fieldName]: value
      }
    });
  };

  // Funzione per gestire il cambio dei campi multiselect
  const handleMultiSelectChange = (fieldName, value, isChecked) => {
    const currentValues = editFormData.formFields[fieldName] || [];

    let updatedValues;
    if (isChecked && !currentValues.includes(value)) {
      updatedValues = [...currentValues, value];
    } else if (!isChecked && currentValues.includes(value)) {
      updatedValues = currentValues.filter(v => v !== value);
    } else {
      updatedValues = currentValues;
    }

    handleFieldChange(fieldName, updatedValues);
  };

  // Funzione per gestire il cambio dei file
  const handleFileChange = (fieldName, file) => {
    handleFieldChange(fieldName, file);
  };

  // Funzione per inviare le modifiche
  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!isRegistrationOpen) {
      setError('Le iscrizioni sono chiuse. Le modifiche non sono consentite.');
      return;
    }
    setSubmitting(true);
    setError(null);

    const teamMembersData = getEditMembersData(editFormData.members);
    const ageCheck = validateTeamMemberAges({
      members: teamMembersData,
      registrationFields: event?.registrationFields,
      formFields: editFormData.formFields,
      eventCategory: event?.category,
      referenceDate: getEventReferenceDate(event)
    });

    if (!ageCheck.valid) {
      setError(ageCheck.message);
      setSubmitting(false);
      return;
    }

    try {
      // Preparazione dei dati per l'aggiornamento
      const formData = new FormData();

      // Dati base della squadra
      const teamBasicData = {
        name: editFormData.name,
        memberIds: editFormData.members
      };

      // Dati del form che non sono file
      const textFormData = {};

      // Separare i campi file dagli altri tipi di dati
      const fileFields = {};

      if (editFormData.formFields) {
        Object.entries(editFormData.formFields).forEach(([key, value]) => {
          if (value instanceof File) {
            fileFields[key] = value;
          } else if (typeof value === 'object' && value !== null && !Array.isArray(value) && value.filename) {
            // Se è un riferimento a un file esistente, lo manteniamo
            textFormData[key] = value;
          } else {
            // Altri tipi di campi
            textFormData[key] = value;
          }
        });
      }

      // Aggiungiamo i dati base
      teamBasicData.formData = textFormData;
      formData.append('teamData', JSON.stringify(teamBasicData));

      // Aggiungiamo i file al FormData
      Object.entries(fileFields).forEach(([fieldName, file]) => {
        formData.append(`field_${fieldName}`, file);
      });

      // Inviamo l'aggiornamento
      await axios.put(`/api/events/${id}/teams/${editingTeam._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Squadra aggiornata con successo!');

      // Aggiorniamo la lista delle squadre
      const teamsResponse = await axios.get(
        `/api/associations/${user.association_id}/events/${id}/teams`
      );

      if (teamsResponse.data && teamsResponse.data.teams) {
        setTeams(teamsResponse.data.teams);
      }

      // Chiudiamo il modal
      handleCloseModal();

      // Rimuoviamo il messaggio di successo dopo 3 secondi
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Errore durante l\'aggiornamento:', err);
      const errorMessage = err.response?.data?.message || 'Errore durante l\'aggiornamento. Riprova più tardi.';
      const errorDetails = err.response?.data?.details || '';

      setError(`${errorMessage}${errorDetails ? ` - ${errorDetails}` : ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Funzione per eliminare una squadra
  const handleDeleteTeam = async (teamId) => {
    if (!isRegistrationOpen) {
      setError('Le iscrizioni sono chiuse. Le modifiche non sono consentite.');
      return;
    }
    if (!window.confirm('Sei sicuro di voler eliminare questa squadra?')) {
      return;
    }

    try {
      await axios.delete(`/api/events/${id}/teams/${teamId}`);

      setSuccess('Squadra eliminata con successo!');

      // Aggiorniamo la lista delle squadre
      const updatedTeams = teams.filter(team => team._id !== teamId);
      setTeams(updatedTeams);

      // Rimuoviamo il messaggio di successo dopo 3 secondi
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Errore durante l\'eliminazione:', err);
      setError('Errore durante l\'eliminazione della squadra. Riprova più tardi.');
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
        <p className="mt-2">Caricamento dati...</p>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Evento non trovato o non accessibile.</Alert>
        <Button as={Link} to="/association/events" variant="secondary">
          Torna agli Eventi
        </Button>
      </Container>
    );
  }

  // Verifica se l'evento è di tipo manifestazione
  const isManifestazioneEvent = event.category === 'manifestazione';

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button as={Link} to={`/association/events/${id}`} variant="outline-secondary">
          &larr; Torna all'Evento
        </Button>
        {isRegistrationOpen ? (
          <Button
            as={Link}
            to={isManifestazioneEvent ? `/association/events/${id}/register` : `/association/events/${id}/team-registration`}
            variant="primary"
          >
            <i className="bi bi-plus-lg me-1"></i>
            {isManifestazioneEvent ? 'Nuova iscrizione' : 'Registra nuova squadra'}
          </Button>
        ) : (
          <Button variant="secondary" disabled title="Iscrizioni chiuse">
            <i className="bi bi-plus-lg me-1"></i>
            {isManifestazioneEvent ? 'Nuova iscrizione' : 'Registra nuova squadra'}
          </Button>
        )}
      </div>

      <Card>
        <Card.Header className="bg-primary text-white">
          <h3>{isManifestazioneEvent ? 'Partecipanti Iscritti' : 'Squadre Iscritte'}</h3>
          <p className="mb-0">{event.title} - {formatDateRange(event.eventDate || event.event_date, event.eventEndDate || event.event_end_date || event.eventDate || event.event_date)}</p>
        </Card.Header>

        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          {!isRegistrationOpen && (
            <Alert variant="warning" className="mb-3">
              Le iscrizioni sono chiuse. Puoi consultare il riepilogo ma non modificare le iscrizioni.
            </Alert>
          )}

          {teams.length === 0 ? (
            <Alert variant="info">
              Non ci sono ancora {isManifestazioneEvent ? 'partecipanti iscritti' : 'squadre iscritte'} per questo evento.
              {isRegistrationOpen && (
                <div className="mt-2">
                  <Button
                    as={Link}
                    to={isManifestazioneEvent ? `/association/events/${id}/register` : `/association/events/${id}/team-registration`}
                    variant="primary"
                  >
                    {isManifestazioneEvent ? 'Iscriviti ora' : 'Registra nuova squadra'}
                  </Button>
                </div>
              )}
            </Alert>
          ) : (
            <>
              <div className="mb-3">
                <Badge bg="info" className="me-2 p-2">
                  <i className="bi bi-people-fill me-1"></i>
                  {teams.length} {isManifestazioneEvent ? 'gruppi di partecipanti' : 'squadre'} iscritti
                </Badge>
                <Badge bg="success" className="p-2">
                  <i className="bi bi-person-fill me-1"></i>
                  {teams.reduce((acc, team) => acc + team.members.length, 0)} partecipanti totali
                </Badge>
              </div>

              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k === 'report' ? 'report' : Number(k))}
                className="mb-4"
              >
                <Tab eventKey="report" title={<span><i className="bi bi-file-earmark-spreadsheet me-1"></i>Report Finale</span>}>
                  <Card className="mb-4 mt-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Riepilogo Iscrizioni Effettuate</h5>
                      <Button variant="outline-success" size="sm" onClick={() => {
                        const table = document.getElementById("report-table");
                        if (!table) return;

                        // Costruisci array di righe leggendo le celle
                        const rows = [];
                        for (let r = 0; r < table.rows.length; r++) {
                          const row = table.rows[r];
                          const cells = [];
                          for (let c = 0; c < row.cells.length; c++) {
                            cells.push(row.cells[c].innerText || '');
                          }
                          rows.push(cells);
                        }

                        // Converti in worksheet e scarica
                        const worksheet = XLSX.utils.aoa_to_sheet(rows);
                        const workbook = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
                        const safeTitle = (event?.title || 'Evento').replace(/[^a-z0-9-_ ]/gi, '_');
                        XLSX.writeFile(workbook, `Report_Iscrizioni_${safeTitle}.xlsx`);
                      }}>
                        <i className="bi bi-download me-1"></i> Esporta XLSX
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      <div className="table-responsive">
                        <Table striped bordered hover id="report-table" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                          <thead className="table-dark">
                            <tr>
                              <th>{isManifestazioneEvent ? 'Nome Partecipante' : 'Nome Squadra'}</th>
                              <th>Associazione</th>
                              <th>Data Iscrizione</th>
                              <th>N. Membri</th>
                              {event.registrationFields?.map(f => (
                                <th key={f.fieldName}>{f.fieldLabel}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {teams.map((t, idx) => (
                              <tr key={t._id || idx}>
                                <td>{t.name}</td>
                                <td>{user?.name || user?.username || 'La tua Associazione'}</td>
                                <td>{new Date(t.registration_date).toLocaleDateString('it-IT')}</td>
                                <td>{t.members?.length || 0}</td>
                                {event.registrationFields?.map(f => {
                                  let val = t.formData?.[f.fieldName] || '-';
                                  // Se il campo è un file mostriamo 'File caricato' / '-'
                                  if (f.fieldType === 'file' || f.fieldType === 'audio') {
                                    val = val !== '-' && val ? 'File caricato' : '-';
                                  } else if (Array.isArray(val)) {
                                    val = val.join(', ');
                                  }
                                  return <td key={f.fieldName}>{String(val)}</td>;
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Tab>

                {teams.map((team, index) => (
                  <Tab
                    key={index}
                    eventKey={index}
                    title={team.name || `Squadra ${index + 1}`}
                  >
                    <Card className="mb-4">
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">{team.name}</h5>
                        <div>
                          <Badge bg="info" className="me-2">
                            Iscrizione: {formatDate(team.registration_date)}
                          </Badge>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditTeam(team, index)}
                            disabled={!isRegistrationOpen}
                            title={!isRegistrationOpen ? 'Iscrizioni chiuse' : undefined}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Modifica
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteTeam(team._id)}
                            disabled={!isRegistrationOpen}
                            title={!isRegistrationOpen ? 'Iscrizioni chiuse' : undefined}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Elimina
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <h6 className="mb-3">Membri della squadra</h6>
                        {team.members && team.members.length > 0 ? (
                          <Table striped bordered hover responsive className="mb-4">
                            <thead>
                              <tr>
                                <th>ID</th>
                                <th>Nome</th>
                                <th>Cognome</th>
                                <th>Data di nascita (Età)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {team.members.map((member, i) => {
                                // Trova i dettagli completi del membro dall'array dei membri
                                const fullMemberInfo = members.find(m => m.id === member.member_id);

                                return (
                                  <tr key={i}>
                                    <td>{member.member_id}</td>
                                    <td>{fullMemberInfo?.first_name || member.first_name || '-'}</td>
                                    <td>{fullMemberInfo?.last_name || member.last_name || '-'}</td>
                                    <td>{formatBirthWithAge(fullMemberInfo?.birth_date || member.birth_date)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        ) : (
                          <Alert variant="warning">Nessun membro nella squadra</Alert>
                        )}

                        {team.formData && Object.keys(team.formData).length > 0 && (
                          <>
                            <h6 className="mb-3">Informazioni aggiuntive</h6>
                            <Row>
                              {Object.entries(team.formData).map(([key, value], i) => {
                                const field = event.registrationFields.find(f => f.fieldName === key);

                                if (!field) return null;

                                return (
                                  <Col md={6} key={i} className="mb-3">
                                    <Card>
                                      <Card.Body>
                                        <h6>{field.fieldLabel}</h6>

                                        {/* Visualizza il valore in base al tipo */}
                                        {(field.fieldType === 'text' || field.fieldType === 'number' || field.fieldType === 'select') && (
                                          <p>{value || '-'}</p>
                                        )}

                                        {field.fieldType === 'multiselect' && Array.isArray(value) && (
                                          <ul>
                                            {value.map((item, idx) => (
                                              <li key={idx}>{item}</li>
                                            ))}
                                          </ul>
                                        )}

                                        {field.fieldType === 'audio' && value && (
                                          <div className="alert alert-success d-flex align-items-center">
                                            <i className="bi bi-file-music me-2"></i>
                                            <div className="d-flex justify-content-between align-items-center w-100">
                                              <span>{value.filename || 'File audio'}</span>
                                              <Badge bg="success">
                                                {value.size ? `${(value.size / 1024).toFixed(1)} KB` : ''}
                                              </Badge>
                                            </div>
                                          </div>
                                        )}

                                        {field.fieldType === 'file' && value && (
                                          <div className="alert alert-info d-flex align-items-center">
                                            <i className="bi bi-file-earmark me-2"></i>
                                            <div className="d-flex justify-content-between align-items-center w-100">
                                              <span>{value.filename || 'Documento'}</span>
                                              <Badge bg="info">
                                                {value.size ? `${(value.size / 1024).toFixed(1)} KB` : ''}
                                              </Badge>
                                            </div>
                                          </div>
                                        )}
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab>
                ))}
              </Tabs>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal per la modifica della squadra */}
      <Modal show={showEditModal} onHide={handleCloseModal} size="lg">
        <Form onSubmit={handleSubmitEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Modifica Squadra</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            {!isRegistrationOpen && (
              <Alert variant="warning" className="mb-3">
                Le iscrizioni sono chiuse. Le modifiche non sono consentite.
              </Alert>
            )}

            <fieldset disabled={!isRegistrationOpen || submitting}>

            {/* Riepilogo squadra selezionata */}
            {editingTeam && (
              <Card className="mb-3 border-primary">
                <Card.Header className="bg-primary text-white py-2">
                  <strong><i className="bi bi-people-fill me-2"></i>Squadra selezionata: {editingTeam.name}</strong>
                </Card.Header>
                <Card.Body className="py-2">
                  {editingTeam.members && editingTeam.members.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {editingTeam.members.map((m, idx) => {
                        const memberInfo = members.find(mem => mem.id === m.member_id);
                        return (
                          <Badge key={idx} bg="secondary" className="fs-6 fw-normal">
                            {memberInfo
                              ? `${memberInfo.first_name} ${memberInfo.last_name}`
                              : (m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : `Socio #${m.member_id}`)}
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-muted fst-italic">Nessun membro nella squadra</span>
                  )}
                </Card.Body>
              </Card>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Nome Squadra</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.name}
                onChange={handleTeamNameChange}
                required
              />
            </Form.Group>

            {/* Membri attualmente selezionati con possibilità di rimozione */}
            {editFormData.members.length > 0 && (
              <div className="mb-3">
                <h5 className="mb-2">Membri selezionati ({editFormData.members.length})</h5>
                <div className="d-flex flex-wrap gap-2">
                  {editFormData.members.map(memberId => {
                    const m = members.find(x => x.id === memberId);
                    return (
                      <Badge
                        key={memberId}
                        bg="primary"
                        className="d-flex align-items-center gap-1 fs-6 fw-normal py-1 px-2"
                        style={{ cursor: 'default' }}
                      >
                        {m ? `${m.first_name} ${m.last_name}` : `#${memberId}`}
                        <span
                          role="button"
                          aria-label="Rimuovi"
                          style={{ cursor: 'pointer', marginLeft: '4px', fontWeight: 'bold' }}
                          onClick={() => handleMemberSelect(memberId)}
                        >&times;</span>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {(() => {
              const ageRules = getActiveAgeRules(
                event?.registrationFields,
                editFormData.formFields,
                event?.category
              );
              if (!ageRules) return null;
              return (
                <Alert variant="info" className="mb-3">
                  <strong>Vincolo età — {ageRules.categoryLabel}</strong>
                  <div className="small mt-1">
                    In fascia: {ageRules.minAge}-{ageRules.maxAge} anni. Massimo {ageRules.maxOutOfQuota} fuori fascia.
                  </div>
                </Alert>
              );
            })()}

            <h5 className="mt-2 mb-3">Tutti i soci disponibili</h5>
            {members.length === 0 ? (
              <Alert variant="warning">
                Non ci sono membri disponibili per l'iscrizione.
              </Alert>
            ) : (
              <>
                <Form.Group className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Cerca socio per nome o cognome..."
                    value={memberSearchQuery}
                    onChange={e => setMemberSearchQuery(e.target.value)}
                  />
                </Form.Group>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}></th>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('name')}
                        >
                          Nome
                          <SortIcon columnKey="name" />
                        </th>
                        <th 
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort('age')}
                        >
                          Data di nascita (Età)
                          <SortIcon columnKey="age" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMembers
                        .filter(member => {
                          const q = memberSearchQuery.trim().toLowerCase();
                          if (!q) return true;
                          return (
                            `${member.first_name} ${member.last_name}`.toLowerCase().includes(q) ||
                            member.first_name.toLowerCase().includes(q) ||
                            member.last_name.toLowerCase().includes(q)
                          );
                        })
                        .map(member => {
                          const ageRules = getActiveAgeRules(
                            event?.registrationFields,
                            editFormData.formFields,
                            event?.category
                          );
                          const isSelected = editFormData.members.includes(member.id);
                          const ageStatus = getMemberAgeStatus(
                            member,
                            ageRules,
                            getEventReferenceDate(event)
                          );
                          const wouldBlock = !isSelected && ageRules && (() => {
                            const check = canAddMemberToTeam(
                              member,
                              getEditMembersData(editFormData.members),
                              ageRules,
                              getEventReferenceDate(event)
                            );
                            return !check.allowed;
                          })();

                          return (
                          <tr key={member.id}>
                            <td className="text-center">
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleMemberSelect(member.id)}
                                disabled={wouldBlock}
                                title={wouldBlock ? 'Limite partecipanti fuori fascia raggiunto' : ''}
                              />
                            </td>
                            <td>
                              {member.first_name} {member.last_name}
                              {ageRules && isSelected && ageStatus === 'out_of_quota' && (
                                <Badge bg="warning" text="dark" className="ms-2">Fuori fascia</Badge>
                              )}
                              {ageRules && isSelected && ageStatus === 'in_quota' && (
                                <Badge bg="success" className="ms-2">In fascia</Badge>
                              )}
                            </td>
                            <td>{formatBirthWithAge(member.birth_date)}</td>
                          </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              </>
            )}

            {event.registrationFields && event.registrationFields.length > 0 && (
              <>
                <h5 className="mt-4 mb-3">Informazioni Aggiuntive</h5>
                <Row>
                  {event.registrationFields.map(field => (
                    <Col md={6} key={field.fieldName} className="mb-3">
                      <Form.Group controlId={`edit-${field.fieldName}`}>
                        <Form.Label>
                          {field.fieldLabel}
                          {field.isRequired && <span className="text-danger">*</span>}
                        </Form.Label>

                        {field.fieldType === 'text' && (
                          <Form.Control
                            type="text"
                            value={editFormData.formFields[field.fieldName] || ''}
                            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                            required={field.isRequired}
                          />
                        )}

                        {field.fieldType === 'number' && (
                          <Form.Control
                            type="number"
                            value={editFormData.formFields[field.fieldName] || ''}
                            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                            required={field.isRequired}
                          />
                        )}

                        {field.fieldType === 'select' && (
                          <Form.Select
                            value={editFormData.formFields[field.fieldName] || ''}
                            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                            required={field.isRequired}
                          >
                            <option value="">Seleziona un'opzione</option>
                            {field.options && field.options.map(option => {
                              const value = getOptionValue(option);
                              const label = getOptionLabel(option);
                              return (
                                <option key={value} value={value}>{label}</option>
                              );
                            })}
                          </Form.Select>
                        )}

                        {field.fieldType === 'multiselect' && field.options && (
                          <div>
                            {field.options.map(option => {
                              const value = getOptionValue(option);
                              const label = getOptionLabel(option);
                              return (
                              <Form.Check
                                key={value}
                                type="checkbox"
                                id={`edit-${field.fieldName}-${value}`}
                                label={label}
                                checked={(editFormData.formFields[field.fieldName] || []).includes(value)}
                                onChange={(e) => handleMultiSelectChange(
                                  field.fieldName,
                                  value,
                                  e.target.checked
                                )}
                              />
                              );
                            })}
                          </div>
                        )}

                        {field.fieldType === 'audio' && (
                          <div>
                            <div className="d-flex">
                              <div className="flex-grow-1">
                                <input
                                  type="file"
                                  className="form-control"
                                  accept="audio/*"
                                  onChange={(e) => handleFileChange(field.fieldName, e.target.files[0])}
                                  id={`edit-audio-file-${field.fieldName}`}
                                />
                              </div>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="ms-2"
                                onClick={() => document.getElementById(`edit-audio-file-${field.fieldName}`).click()}
                              >
                                <i className="bi bi-file-music me-1"></i>
                                Seleziona file
                              </Button>
                            </div>
                            <Form.Text className="text-muted">
                              Carica un nuovo file audio o mantieni quello esistente
                            </Form.Text>
                            {/* Visualizza il file attuale se presente */}
                            {editFormData.formFields[field.fieldName] && (
                              typeof editFormData.formFields[field.fieldName] === 'object' &&
                              !editFormData.formFields[field.fieldName].name &&
                              editFormData.formFields[field.fieldName].filename) && (
                                <div className="mt-2">
                                  <div className="alert alert-success d-flex align-items-center">
                                    <i className="bi bi-file-music me-2"></i>
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                      <span>{editFormData.formFields[field.fieldName].filename}</span>
                                      <div>
                                        <span className="badge bg-success me-2">
                                          File esistente
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}

                        {field.fieldType === 'file' && (
                          <div>
                            <div className="d-flex">
                              <div className="flex-grow-1">
                                <input
                                  type="file"
                                  className="form-control"
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                  onChange={(e) => handleFileChange(field.fieldName, e.target.files[0])}
                                  id={`edit-file-${field.fieldName}`}
                                />
                              </div>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="ms-2"
                                onClick={() => document.getElementById(`edit-file-${field.fieldName}`).click()}
                              >
                                <i className="bi bi-file-earmark me-1"></i>
                                Seleziona file
                              </Button>
                            </div>
                            <Form.Text className="text-muted">
                              Carica un nuovo documento o mantieni quello esistente
                            </Form.Text>
                            {/* Visualizza il file attuale se presente */}
                            {editFormData.formFields[field.fieldName] &&
                              typeof editFormData.formFields[field.fieldName] === 'object' &&
                              !editFormData.formFields[field.fieldName].name &&
                              editFormData.formFields[field.fieldName].filename && (
                                <div className="mt-2">
                                  <div className="alert alert-info d-flex align-items-center">
                                    <i className="bi bi-file-earmark me-2"></i>
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                      <span>{editFormData.formFields[field.fieldName].filename}</span>
                                      <div>
                                        <span className="badge bg-info me-2">
                                          File esistente
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </>
            )}
            </fieldset>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Annulla
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting || !isRegistrationOpen}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Salvataggio...
                </>
              ) : (
                'Salva modifiche'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default TeamRegistrationDetails;
